import { getEndTenorLegsFromParsedPlot } from 'lib/getFormattedLegs';
import { tenorToYears, BOND_TYPE_ENUM } from 'lib/constants';
import { flattenStructureLegPieces } from '@/helper/plot.helper';

const FWD_KEYWORD_PATTERN = /^f(?:wd)?$/i;
const TENOR_TOKEN_PATTERN = /^(\d+(?:\.\d+)?)([ymwd])$/i;
const COMPACT_FWD_PAIR_PATTERN = /^(\d+(?:\.\d+)?)([ymwd])(\d+(?:\.\d+)?)([ymwd])$/i;
const IMM_TOKEN_PATTERN = /^[zmuh]\d{1,2}$/i;
export const aud3mCurveName = 'AUD IRS (Vs 3M)';
// The single AUD IRS book; each leg prices off Vs 3M (QQ) or Vs 6M (SS) by its end date.
export const AUD_IRS_3M6M_NAME = 'AUD IRS (3M/6M)';
export const isAud3m6mCurveName = (name) => name === AUD_IRS_3M6M_NAME;
export const AUD_IRS_SHORT_TENOR_YEARS = 3;
const AUD_VS_SUFFIX_PATTERN = /\(\s*Vs\s+\d+M\s*\)/i;

const normaliseTenors = (tenors) => {
    if (!Array.isArray(tenors)) return [];
    return tenors.filter((t) => t != null && String(t).trim() !== '').map((t) => t.replace(' ', ''));
};

export const isAudDomesticConventionType = (type) => (
    type === BOND_TYPE_ENUM.AUD_MIX_MATCH_SPREAD
    || type === BOND_TYPE_ENUM.AUD_MIX_MATCH_BUTTERFLY
);

export const classifyLeg = (tenor) => {
    const years = tenorToYears(tenor);
    if (!Number.isFinite(years)) return null;
    return years <= AUD_IRS_SHORT_TENOR_YEARS ? 'QQ' : 'SS';
};

export const CONVENTION_DESCRIPTIONS = {
    QQ: 'quarterly fixed vs 3M BBSW',
    SS: 'semi-annual fixed vs 6M BBSW'
};

// One entry per leg of a book row ("2y x 5y") with its QQ/SS from the row's convention ('QQ/SS').
export const getConventionLegs = (tenor, convention) => {
    const codes = convention?.split('/') || [];
    return tenor.split(' x ').map((leg, index) => ({
        tenor: leg.replace(/\s+/g, '').toUpperCase(),
        code: codes[index] || null
    }));
};

// Every QQ/SS combination for a structure, in leg order: QQ/QQ, QQ/SS, SS/QQ, SS/SS.
export const getConventionOptions = (legCount) => Array.from({ length: 2 ** legCount }, (_, combo) => (
    Array.from({ length: legCount }, (__, leg) => (Math.floor(combo / 2 ** (legCount - 1 - leg)) % 2 ? 'SS' : 'QQ')).join('/')
));

export const isAudIrsCurveName = (name) => {
    if (!name || typeof name !== 'string') return false;
    return /^\s*aud\s+irs\s*\(\s*vs\s+[36]m\s*\)\s*$/i.test(name);
};

export const isAudIrsVs3M = (name) => /\(\s*vs\s+3m\s*\)/i.test(name || '');
export const isAudIrsVs6M = (name) => /\(\s*vs\s+6m\s*\)/i.test(name || '');

export const isOutright = (tenors) => normaliseTenors(tenors).length === 1;

export const isMixedMultiLeg = (tenors) => {
    const list = normaliseTenors(tenors);
    if (list.length < 2) return false;
    const labels = list.map(classifyLeg).filter(Boolean);
    return labels.includes('QQ') && labels.includes('SS');
};

export const isSameSideMultiLeg = (tenors) => {
    const list = normaliseTenors(tenors);
    if (list.length < 2) return false;
    return !isMixedMultiLeg(list);
};

export const shouldShowDomesticConvention = ({ tenors, curveName } = {}) => {
    if (!isAudIrsCurveName(curveName)) return false;
    const list = normaliseTenors(tenors);
    if (list.length === 0) return false;
    return !isLaunchedCurveMatchingConvention({ tenors: list, launchedCurveName: curveName });
};

export const resolveConventionCurveName = (tenor) => {
    const label = classifyLeg(tenor);
    if (!label) return null;
    return label === 'QQ' ? 'AUD IRS (Vs 3M)' : 'AUD IRS (Vs 6M)';
};

export const isLaunchedCurveMatchingConvention = ({ tenors, launchedCurveName } = {}) => {
    const list = normaliseTenors(tenors);
    if (!list.length || !isAudIrsCurveName(launchedCurveName)) return false;
    return list.every((tenor) => {
        const label = classifyLeg(tenor);
        if (!label) return false;
        if (label === 'QQ') return isAudIrsVs3M(launchedCurveName);
        return isAudIrsVs6M(launchedCurveName);
    });
};

export const formatConventionSuffix = (tenors) => {
    const list = normaliseTenors(tenors);
    if (!list.length) return '';
    return list.map(classifyLeg).filter(Boolean).join(' ');
};

export const stripAudVsSuffix = (name) => {
    if (!name || typeof name !== 'string') return '';
    return name.replace(/\s*\(\s*Vs\s+\d+M\s*\)/i, '').trim() || name;
};

const sortMixMatchTenorsAscending = (tenors) => (
    [...tenors].sort((a, b) => tenorToYears(a) - tenorToYears(b))
);

const getMixMatchStructureLegTenors = (endLabel, end1Label, end2Label) => {
    if (end2Label) return sortMixMatchTenorsAscending([endLabel, end1Label, end2Label]);
    if (end1Label) return sortMixMatchTenorsAscending([endLabel, end1Label]);
    return endLabel ? [endLabel] : [];
};

const applyTenorCase = (label, tenorCase) => {
    if (tenorCase === 'upper') return label.toUpperCase();
    if (tenorCase === 'preserve') return label;
    return label.toLowerCase();
};

export const formatMixMatchSecurityName = ({
    bondName,
    startLabel,
    endLabel,
    end1Label,
    end2Label,
    tenorCase = 'lower'
} = {}) => {
    const baseName = stripAudVsSuffix(bondName) || 'AUD IRS';
    const orderedTenors = getMixMatchStructureLegTenors(endLabel, end1Label, end2Label);
    const legLabels = orderedTenors.map(classifyLeg).filter(Boolean).join(' ');
    const startRaw = String(startLabel || 'Spot').trim().replace(/\s+/g, '');
    const start = startRaw.toLowerCase() === 'spot' ? 'Spot' : startRaw;
    const tenorsStr = orderedTenors
        .map((t) => applyTenorCase(String(t).trim().replace(/\s+/g, ''), tenorCase))
        .join(' X ');
    const structure = tenorsStr ? `${start} - ${tenorsStr}` : start;
    return legLabels ? `${baseName} (${legLabels}) | ${structure}` : `${baseName} | ${structure}`;
};

export const formatAudIrsCommandHeader = ({
    tenors,
    structureInput,
    parsed,
    useConvention,
    launchedCurveName
} = {}) => {
    const list = structureInput != null
        ? resolveLabelOrderTenors(structureInput, parsed)
        : normaliseTenors(tenors);
    const tenorStr = list.join(' ');
    const base = stripAudVsSuffix(launchedCurveName) || 'AUD IRS';

    if (useConvention && list.length > 0) {
        const suffix = formatConventionSuffix(list);
        if (suffix) {
            return tenorStr ? `${base} (${suffix}) ${tenorStr}` : `${base} (${suffix})`;
        }
    }

    const launched = (launchedCurveName || base).trim();
    return tenorStr ? `${launched} ${tenorStr}` : launched;
};

export const shouldUseMixMatchDispatch = (showDomesticConvention, domesticConvention) => (
    showDomesticConvention && Boolean(domesticConvention)
);

export const flattenPlotLegTenors = (tenors) => {
    if (!Array.isArray(tenors) || !tenors.length) return [];
    return Array.isArray(tenors[0]) ? tenors.flat() : tenors;
};

export const extractStructureEndTenors = (tenors, { isForward = false } = {}) => {
    if (!Array.isArray(tenors) || !tenors.length) return [];
    if (Array.isArray(tenors[0])) {
        return normaliseTenors(
            tenors
                .map((pair) => (Array.isArray(pair) && pair.length >= 2 ? pair[1] : null))
                .filter(Boolean)
        );
    }
    if (isForward) return normaliseTenors(tenors.filter((_, idx) => idx % 2 !== 0));
    return normaliseTenors(tenors);
};

const endTenorFromToken = (token) => {
    const compact = String(token).match(COMPACT_FWD_PAIR_PATTERN);
    if (compact) return `${compact[3]}${compact[4]}`;
    return TENOR_TOKEN_PATTERN.test(token) ? token : null;
};

/** Label-order end tenors from structure input; falls back to getEndTenorLegsFromParsedPlot. */
export const resolveLabelOrderTenors = (structureInput, parsed = null) => {
    const raw = String(structureInput ?? '').trim();
    if (raw) {
        const tokens = raw.split(/\s+/).filter(Boolean);
        const fwdIndex = tokens.findIndex((t) => FWD_KEYWORD_PATTERN.test(t));
        const legTokens = (fwdIndex >= 0
            ? tokens.filter((t) => !FWD_KEYWORD_PATTERN.test(t) && !IMM_TOKEN_PATTERN.test(t))
            : tokens.filter((t) => !IMM_TOKEN_PATTERN.test(t))
        );
        const pieces = flattenStructureLegPieces(legTokens);

        if (fwdIndex >= 0) {
            if (pieces.length === 2 && pieces.every((t) => TENOR_TOKEN_PATTERN.test(t))) {
                return normaliseTenors([pieces[1]]);
            }
            const ends = pieces.map(endTenorFromToken).filter(Boolean);
            if (ends.length) return normaliseTenors(ends);
        } else if (pieces.length) {
            const ends = pieces.map(endTenorFromToken).filter(Boolean);
            if (ends.length) return normaliseTenors(ends);
        }
    }

    const csv = parsed?.isValid ? getEndTenorLegsFromParsedPlot(parsed) : null;
    if (csv) return normaliseTenors(csv.split(','));

    return extractStructureEndTenors(parsed?.tenors || [], { isForward: Boolean(parsed?.isFwd || parsed?.imm) });
};

export const resolveAudMixMatchSideContext = ({
    curveName,
    structureInput = '',
    parsed = null,
    useMixMatchDispatch = false
} = {}) => {
    const isForward = Boolean(parsed?.isFwd || parsed?.imm);
    const endTenors = extractStructureEndTenors(parsed?.tenors || [], { isForward });
    const showDC = shouldShowDomesticConvention({ tenors: endTenors, curveName });
    const dispatch = shouldDispatchMixMatchForStructure({
        useMixMatchDispatch,
        tenors: parsed?.tenors || [],
        curveName,
        isForward
    });
    const displayCurveName = formatMixMatchDisplayLabel(curveName, endTenors, useMixMatchDispatch, {
        structureInput,
        parsed
    });
    const chartCurveName = (dispatch ? displayCurveName : curveName)?.toUpperCase() || '';

    return {
        parsed,
        endTenors,
        showDC,
        dispatch,
        displayCurveName,
        chartCurveName
    };
};

export const findAudIrsCurveEntries = (irsList = []) => ({
    audIrsVs3MEntry: irsList.find((i) => isAudIrsVs3M(i?.label || i?.value?.name)),
    audIrsVs6MEntry: irsList.find((i) => (
        !isAudIrsVs3M(i?.label || i?.value?.name)
        && isAudIrsCurveName(i?.label || i?.value?.name)
    ))
});

export const resolveConventionTargetCurveId = ({
    useMixMatchDispatch,
    tenors,
    audIrsVs3MEntry,
    audIrsVs6MEntry,
    returnValueObject = false
} = {}) => {
    if (!useMixMatchDispatch) return null;
    const list = normaliseTenors(tenors);
    if (list.length !== 1) return null;
    const label = classifyLeg(list[0]);
    const entry = label === 'QQ' ? audIrsVs3MEntry : audIrsVs6MEntry;
    if (!entry?.value) return null;
    const resolvedId = typeof entry.value === 'object' && entry.value !== null
        ? entry.value.id
        : entry.value;
    return returnValueObject ? entry.value : resolvedId;
};

export const resolveAudMixMatchRcCurveId = ({
    useMixMatchDispatch,
    launchedCurveName,
    launchedId,
    endTenors,
    audIrsVs3MEntry,
    audIrsVs6MEntry
} = {}) => {
    if (!launchedId) return launchedId;
    if (!useMixMatchDispatch || !isAudIrsCurveName(launchedCurveName)) return launchedId;

    const list = normaliseTenors(Array.isArray(endTenors) ? endTenors : []);
    if (!list.length) return launchedId;

    const labels = list.map(classifyLeg).filter(Boolean);
    if (labels.includes('QQ') && labels.includes('SS')) return launchedId;

    const side = labels[0];
    const conventionId = resolveAudMixMatchCurveId(
        side === 'QQ' ? audIrsVs3MEntry : audIrsVs6MEntry
    );
    if (!conventionId) return launchedId;

    if ((side === 'QQ' && isAudIrsVs3M(launchedCurveName))
        || (side === 'SS' && !isAudIrsVs3M(launchedCurveName))) {
        return launchedId;
    }
    return conventionId;
};

export const formatMixMatchDisplayLabel = (label, tenors, useMixMatchDispatch, labelOptions = null) => {
    if (!useMixMatchDispatch || !label) return label;
    const suffixTenors = labelOptions?.structureInput != null
        ? resolveLabelOrderTenors(labelOptions.structureInput, labelOptions.parsed)
        : normaliseTenors(tenors);
    const suffix = formatConventionSuffix(suffixTenors);
    if (!suffix) return label;
    return label.replace(AUD_VS_SUFFIX_PATTERN, `(${suffix})`);
};

export const formatTenorForIrsApi = (tenor) => (
    String(tenor).toUpperCase().replace(/(\d+)([YMWD])/gi, '$1 $2')
);

export const combineMixMatchLegSeries = (
    legSeriesList,
    isButterfly,
    spreadSubtractMode = 'endMinusStart'
) => {
    if (!Array.isArray(legSeriesList) || legSeriesList.some((s) => !Array.isArray(s) || !s.length)) {
        return [];
    }
    const indexByDate = legSeriesList.map((series) => {
        const map = new Map();
        series.forEach((row) => { if (row?.d != null) map.set(row.d, Number(row.v)); });
        return map;
    });
    const dates = legSeriesList[0].map((row) => row?.d).filter((d) => d != null);
    return dates.reduce((acc, d) => {
        const values = indexByDate.map((map) => map.get(d));
        if (values.some((v) => !Number.isFinite(v))) return acc;
        let v;
        if (isButterfly) {
            v = (2 * values[1] - values[0] - values[2]) * 100;
        } else {
            v = spreadSubtractMode === 'pairOrder'
                ? (values[0] - values[1]) * 100
                : (values[1] - values[0]) * 100;
        }
        acc.push({ d, v: v.toFixed(4) });
        return acc;
    }, []);
};

export const resolveAudMixMatchCurveId = (entry) => {
    if (!entry?.value) return null;
    const raw = entry.value;
    return typeof raw === 'object' && raw !== null ? raw.id : raw;
};

const defaultMixMatchTargetIdResolver = (label, audIrsVs3MEntry, audIrsVs6MEntry) => {
    const entry = label === 'QQ' ? audIrsVs3MEntry : audIrsVs6MEntry;
    return resolveAudMixMatchCurveId(entry);
};

export const fetchMixMatchSpotPerLegSeries = async ({
    token,
    tenors,
    audIrsVs3MEntry,
    audIrsVs6MEntry,
    getIrsHistoriesUrl,
    formatLeg = (tenor) => tenor,
    conversion
}) => {
    if (!audIrsVs3MEntry?.value || !audIrsVs6MEntry?.value) return null;
    const requests = tenors.map((tenor) => {
        const label = classifyLeg(tenor);
        const targetId = defaultMixMatchTargetIdResolver(
            label,
            audIrsVs3MEntry,
            audIrsVs6MEntry
        );
        if (!targetId) return Promise.resolve({ status: false });
        const params = { id: targetId, legs: formatLeg(tenor) };
        if (conversion !== undefined) params.conversion = conversion;
        return getIrsHistoriesUrl(token, params);
    });
    const responses = await Promise.all(requests);
    if (responses.some((r) => !r?.status || !r?.data?.status)) return null;
    return responses.map((r) => r.data.entity || []);
};

export const fetchMixMatchForwardPerPairSeries = async ({
    token,
    legArray,
    audIrsVs3MEntry,
    audIrsVs6MEntry,
    getIrsForwardHistoriesUrl,
    buildPairLegs = (start, end) => `${start},${end}`,
    getForwardParams = () => ({})
}) => {
    if (!audIrsVs3MEntry?.value || !audIrsVs6MEntry?.value) return null;
    const pairs = [];
    for (let i = 0; i + 1 < legArray.length; i += 2) pairs.push([legArray[i], legArray[i + 1]]);
    if (!pairs.length) return null;
    const requests = pairs.map(([start, end]) => {
        const label = classifyLeg(end);
        const targetId = defaultMixMatchTargetIdResolver(
            label,
            audIrsVs3MEntry,
            audIrsVs6MEntry
        );
        if (!targetId) return Promise.resolve({ status: false });
        return getIrsForwardHistoriesUrl(token, {
            id: targetId,
            legs: buildPairLegs(start, end),
            ...getForwardParams(start, end)
        });
    });
    const responses = await Promise.all(requests);
    if (responses.some((r) => !r?.status || !r?.data?.status)) return null;
    return responses.map((r) => r.data.entity || []);
};

export const fetchIrsHistoryWithMixMatch = async ({
    token,
    plotData,
    sideDispatch = false,
    audIrsVs3MEntry,
    audIrsVs6MEntry,
    getIrsHistoriesUrl,
    getIrsForwardHistoriesUrl,
    transformEntity,
    formatLeg = formatTenorForIrsApi,
    buildPairLegs = (start, end) => formatLeg(`${start},${end}`),
    getForwardParams = () => ({ plotRange: { unit: 'Y', value: 10 }, isImm: true }),
    onFetchError = () => {}
}) => {
    const refPlotId = plotData?.id;
    const refFwd = plotData?.isFwd || plotData?.imm;
    const term = Array.isArray(plotData?.term) ? plotData.term : [];
    const refLegs = term.map((x) => x.toUpperCase().replace(/(\d+)([YMWD])/i, '$1 $2')).join(',');

    const mapEntity = (entity) => (entity != null ? transformEntity(entity) : null);

    if (sideDispatch) {
        if (!refFwd && term.length === 1) {
            const targetId = resolveConventionTargetCurveId({
                useMixMatchDispatch: true,
                tenors: term,
                audIrsVs3MEntry,
                audIrsVs6MEntry
            });
            if (targetId) {
                const res = await getIrsHistoriesUrl(token, { id: targetId, legs: refLegs });
                if (!res?.status || !res?.data?.status) {
                    onFetchError();
                    return null;
                }
                return mapEntity(res.data.entity);
            }
        }
        if (!refFwd && (term.length === 2 || term.length === 3)) {
            if (!audIrsVs3MEntry?.value || !audIrsVs6MEntry?.value) return null;
            const legSeries = await fetchMixMatchSpotPerLegSeries({
                token,
                tenors: term,
                audIrsVs3MEntry,
                audIrsVs6MEntry,
                getIrsHistoriesUrl,
                formatLeg
            });
            if (!legSeries) {
                onFetchError();
                return null;
            }
            const combined = combineMixMatchLegSeries(legSeries, term.length === 3);
            return mapEntity(combined);
        }
        if (refFwd && (term.length === 2 || term.length === 4 || term.length === 6)) {
            if (!audIrsVs3MEntry?.value || !audIrsVs6MEntry?.value) return null;
            const legSeries = await fetchMixMatchForwardPerPairSeries({
                token,
                legArray: term,
                audIrsVs3MEntry,
                audIrsVs6MEntry,
                getIrsForwardHistoriesUrl,
                buildPairLegs,
                getForwardParams
            });
            if (!legSeries) {
                onFetchError();
                return null;
            }
            if (legSeries.length === 1) return mapEntity(legSeries[0]);
            const combined = combineMixMatchLegSeries(legSeries, legSeries.length === 3, 'pairOrder');
            return mapEntity(combined);
        }
    }

    let res;
    if (refFwd) {
        res = await getIrsForwardHistoriesUrl(token, {
            id: refPlotId,
            legs: refLegs,
            ...getForwardParams(),
            isImm: refFwd
        });
    } else {
        res = await getIrsHistoriesUrl(token, { id: refPlotId, legs: refLegs });
    }
    if (!res?.status || !res?.data?.status) {
        onFetchError();
        return null;
    }
    return mapEntity(res.data.entity);
};

export const getAnchorPairEndTenor = (legs) => {
    const list = normaliseTenors(Array.isArray(legs) ? legs : String(legs || '').split(','));
    const withoutSpot = list.filter((t) => String(t).toLowerCase() !== 'spot');
    if (!withoutSpot.length) return list[list.length - 1] || null;
    return withoutSpot[withoutSpot.length - 1];
};

export const shouldUseAudMixMatchAnchorPairFromCurve = (legs, curveKind) => {
    const endTenor = getAnchorPairEndTenor(legs);
    if (!endTenor) return false;
    const label = classifyLeg(endTenor);
    if (!label) return false;
    return curveKind === '3M' ? label === 'QQ' : label === 'SS';
};

export const mergeAudMixMatchAnchorRcMaps = (aud3MRc, aud6MRc) => {
    const merged = new Map();
    const addFrom = (rcMap, curveKind) => {
        if (!(rcMap instanceof Map)) return;
        rcMap.forEach((value, key) => {
            const legs = String(key).split(',').filter(Boolean);
            if (!shouldUseAudMixMatchAnchorPairFromCurve(legs, curveKind)) return;
            if (!merged.has(key)) merged.set(key, value);
        });
    };
    addFrom(aud3MRc, '3M');
    addFrom(aud6MRc, '6M');
    return merged;
};

export const shouldDispatchMixMatchForStructure = ({
    useMixMatchDispatch,
    tenors,
    curveName,
    isForward = false
} = {}) => {
    if (!useMixMatchDispatch || !curveName) return false;
    const endTenors = extractStructureEndTenors(
        Array.isArray(tenors) ? tenors : [],
        { isForward }
    );
    if (!shouldShowDomesticConvention({ tenors: endTenors, curveName })) return false;
    return !isLaunchedCurveMatchingConvention({ tenors: endTenors, launchedCurveName: curveName });
};
